import { useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import LangRouter from "./router/LangRouter";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { Suspense, lazy, useEffect } from "react";
import Navbar from "./components/feature/Navbar";
import Footer from "./components/feature/Footer";
import { CartProvider } from "./hooks/useCart";
import FloatingCart from "./components/feature/FloatingCart";
import AppErrorBoundary from "./components/base/AppErrorBoundary";
import VisitTracker from "./components/base/VisitTracker";
import { isAdminPath } from "./pages/admin/adminPath";

/* Lazy load heavy background components for faster initial paint */
const BioluminescentParticles = lazy(() => import("./components/base/BioluminescentParticles"));
const SeaLifeBackground = lazy(() => import("./components/base/SeaLifeBackground"));
const SeabedTreasure = lazy(() => import("./components/base/SeabedTreasure"));
const AdminPage = lazy(() => import("./pages/admin/page"));

/* Internal tools and printable pages end without the sea floor */
const NO_SEABED = ["/reservas", "/ticket"];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => document.getElementById('main-content')?.focus());
  }, [pathname]);
  return null;
}

function AppContent() {
  const { pathname } = useLocation();
  const isCheckoutSurface = pathname === '/carrinho' || pathname === '/pagamento/retorno';

  // The admin is a work tool at a private address (see pages/admin/adminPath.ts): it renders on its own,
  // without the public navbar, tour categories, footer or cart.
  if (isAdminPath(pathname)) {
    return (
      <>
        <ScrollToTop />
        <main id="main-content" tabIndex={-1}>
          <AppErrorBoundary resetKey={pathname}>
            <Suspense fallback={null}>
              <AdminPage />
            </Suspense>
          </AppErrorBoundary>
        </main>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <VisitTracker />
      <Suspense fallback={null}>
        <BioluminescentParticles />
        <SeaLifeBackground />
      </Suspense>
      <div className="min-h-screen flex flex-col bg-deep text-pearl relative">
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute inset-0 future-page-surface" />
        </div>
        <div className="relative z-10 flex flex-col min-h-screen">
          <Navbar />
          <main id="main-content" className="flex-grow" tabIndex={-1}>
            <AppErrorBoundary resetKey={pathname}>
              <AppRoutes />
            </AppErrorBoundary>
          </main>
          {!NO_SEABED.includes(pathname) && (
            <Suspense fallback={null}>
              <SeabedTreasure />
            </Suspense>
          )}
          <Footer />
          {!isCheckoutSurface && <FloatingCart />}
        </div>
      </div>
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <LangRouter>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </LangRouter>
    </I18nextProvider>
  );
}

export default App;
